/** @jsxImportSource @emotion/react **/
import React, { FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";

import { LANG_ALL, styleContainer, validURL } from "../../../pages/utils";
import { Context } from "../../../context/FoundersContext";
import { BaseInput, Text, TextareaInput } from "../../atoms";
import { ContentSection } from "../../organisms";
import MediaField from "../MediaField";
import * as styles from "../Header/index.styles";

const FoundersForm: FC = () => {
  const {
    formData,
    handleChangeFormData: handleChange,
    handleUpdateFormData: handleData,
  } = useContext(Context);

  const { id } = useParams();

  const detail_data = useSelector((state: any) => state.get_founders.data);
  const enterprises = useSelector((state: any) => state.get_enterprises.data);

  const news_data = id ? detail_data?.data?.find((r: any) => r.id === id) : null;

  const [enterpriseOptions, setEnterpriseOptions] = useState<any[]>([]);
  const [isTitleValid, setTitleValid] = useState<boolean>(false);
  const [isNameValid, setNameValid] = useState<boolean>(false);
  const [isLinkedinValid, setLinkedinValid] = useState<boolean>(false);

  type EnterpriseExtract = {
    id: string;
    title: string;
    name?: string;
    lang: string;
  };

  useEffect(() => {
    const constructedOptions =
      enterprises?.data?.map(({ title, name, lang, id }: EnterpriseExtract) => ({
        label: `${name || title} - ${lang}`,
        value: id,
      })) || [];

    setEnterpriseOptions(constructedOptions);

    if (id && news_data) {
      handleData(news_data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, news_data, enterprises]);

  useEffect(() => {
    setTitleValid(formData.full_name.length > 2);
  }, [formData.full_name]);

  useEffect(() => {
    setNameValid(formData.job_position.length > 0);
  }, [formData.job_position]);

  useEffect(() => {
    const temp = validURL(formData.linkedin);
    setLinkedinValid(temp);
  }, [formData.linkedin]);

  const langInit = news_data?.lang
    ? { value: news_data.lang, label: news_data.lang }
    : { value: "", label: "" };

  const enterpriseInit = news_data?.enterprise?.id
    ? {
        value: news_data.enterprise.id,
        label: `${news_data.enterprise.name || news_data.enterprise.title} - ${
          news_data.enterprise.lang
        }`,
      }
    : { value: "", label: "" };

  const handleLang = (e: any) => {
    handleChange({ lang: e.value });
  };

  const handleEnterprise = (e: any) => {
    handleChange({
      enterprise: {
        id: e.value,
      },
    });
  };

  return (
    <ContentSection
      style={styleContainer}
      header={
        <React.Fragment>
          <Text textType="h4" weight="black">
            {id ? "Editar" : "Nuevo"} Fundador
          </Text>
        </React.Fragment>
      }
    >
      <BaseInput
        label="Nombres"
        value={formData.full_name}
        onChange={(value) => handleChange({ full_name: value })}
        placeholder="Nombre completo"
        maxLength={500}
        error={!isTitleValid && formData.full_name.length > 0}
        success={isTitleValid}
        hint={
          !isTitleValid && formData.full_name.length > 0
            ? "Digite más de dos letras"
            : ""
        }
      />

      <BaseInput
        label="Posición"
        value={formData.job_position}
        onChange={(value) => handleChange({ job_position: value })}
        placeholder="Posición"
        maxLength={500}
        error={!isNameValid && formData.job_position.length > 0}
        success={isNameValid}
        hint={
          !isNameValid && formData.job_position.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <TextareaInput
        label="Descripción"
        value={formData.comment ? formData.comment : ""}
        onChange={(value) => handleChange({ comment: value })}
        placeholder="Escribe la descripción larga"
        maxLength={500}
      />

      <MediaField
        label="Imagen del fundador"
        value={formData.image}
        folder="founders"
        onChange={(url) => handleChange({ image: url })}
      />

      <BaseInput
        label="Linkedin"
        value={formData.linkedin}
        onChange={(value) => handleChange({ linkedin: value })}
        placeholder="URL de Linkedin"
        maxLength={1500}
        error={!isLinkedinValid && formData.linkedin.length > 0}
        success={isLinkedinValid}
        hint={
          !isLinkedinValid && formData.linkedin.length > 0
            ? "Introduce una URL. Ej.: https://linkedin.com/in/nombre"
            : ""
        }
      />

      <Text textType="label">
        EMPRESA:
        <Select
          css={styles.input}
          options={enterpriseOptions}
          defaultValue={enterpriseInit}
          onChange={handleEnterprise}
          placeholder="Selecciona una empresa"
        />
      </Text>

      <Text textType="label">
        IDIOMA:
        <Select
          css={styles.input}
          options={LANG_ALL}
          defaultValue={langInit}
          onChange={handleLang}
          placeholder="Selecciona idioma"
        />
      </Text>
    </ContentSection>
  );
};

export default FoundersForm;